import * as React from 'react';
import { useEffect, useState } from 'react';
import Col from 'reactstrap/lib/Col';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import Row from 'reactstrap/lib/Row';
import { deleteProspectUser } from '../../../../api/ProspectUser';
import {
    getProspectUserProfile,
    updateProspectUserProfile,
    ProspectUserProfile
} from '../../../../api/ProspectUserProfile';
import { refreshJwt } from '../../../../api/Shared';
import { useFetch } from '../../../../hooks/useFetch';
import { browserHistory } from '../../../../utilities/history';
import { clearToken } from '../../../../utilities/identity';
import { getSourceData } from '../../../../utilities/sourceData';
import { formatPhoneNumber } from '../../../../utilities/Utils';
import Button from '../../../common/form/Button';
import Form from '../../../common/form/Form';
import ProfilePageInput from '../../../common/form/ProfilePageInput';
import FormValidationDisplay from '../../../common/form/ValidationDisplay';
import LoadingOverlay, { LoadingDisplay } from '../../../common/LoadingOverlay';
import { prospectPages, prospectPath } from '../ProspectRouter';
import { DualRoutedProps, TourPage, tourSignupPath } from '../signup/TourRouter';
import ContactPreferencesCheckboxes from './ContactPreferencesCheckboxes';

interface Props extends DualRoutedProps { }

const MyProfilePage = (props: Props) => {
    const [wasFormValidated, setWasFormValidated] = useState<boolean>(false);
    const [deletionModalOpen, setDeletionModalOpen] = useState(false);
    const [deletionLoading, setDeletionLoading] = useState(false);
    const [deletionError, setDeletionError] = useState<string | undefined>();

    const propertySlug = props.match.params.propertySlug;

    const {
        data: profileData,
        loading: isLoading,
        setData: setProfileData,
        error: errorMessage,
        setLoading,
        setError
    } = useFetch<ProspectUserProfile, undefined>(() => getProspectUserProfile(), [], undefined);

    const validateEmail = (email: string): boolean => {
        const emailPattern = /^(?!.*\.{2})[a-zA-Z0-9._%+-]+(?:\.[a-zA-Z0-9._%+-]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
    };

    const onSubmit = async () => {
        if (profileData && !validateEmail(profileData.email)) {
            setError("Please enter a valid email address.");
            return;
        } 
        if (!profileData) {
            return;
        }
        setError("")
        setLoading(true);

        let result = await updateProspectUserProfile({
            ...profileData,
            contactPreference: profileData.contactPreference || 3
        });

        if (result.success) {
            await refreshJwt();
            setLoading(false);
            browserHistory.push(
                propertySlug
                    ? tourSignupPath(propertySlug)(TourPage.GuestCard)
                    : prospectPath(prospectPages.profile)
            );
        } else {
            setError(result.error);
            setLoading(false);
        }
    };

    const onInputChange = (name: string, value: string) =>
        profileData && setProfileData({ ...profileData, [name]: value });

    const logOut = () => {
        clearToken();
        browserHistory.push(prospectPath(prospectPages.welcome));
    };

    const handleDeleteUser = async () => {
        setDeletionLoading(true);

        let deleteResult = await deleteProspectUser();

        if (deleteResult.success) {
            logOut();
        } else {
            setDeletionError(deleteResult.error);
            setDeletionLoading(false);
        }
    };

    useEffect(() => {
        if (!isLoading && profileData) {
            var sourceData = getSourceData();
            var cachedProfile: ProspectUserProfile = {
                ...profileData,
                email: profileData.email.length > 0 ? profileData.email : sourceData.email || '',
                firstName:
                    profileData.firstName.length > 0
                        ? profileData.firstName
                        : sourceData.first_name || '',
                lastName:
                    profileData.lastName.length > 0
                        ? profileData.lastName
                        : sourceData.last_name || '',
                phone: profileData.phone.length > 0 ? profileData.phone : sourceData.phone || ''
            };
            setProfileData(cachedProfile);
        }
    }, [isLoading]);

    return (
        <div>
            <Row>
                <Col xs={12} lg={propertySlug ? 12 : { offset: 2, size: 8 }}>
                    <LoadingOverlay show={isLoading} inBootstrapColumn={true} />
                    {profileData && (
                        <div>
                            <Form
                                wasFormValidated={wasFormValidated}
                                inBootstrapColumn={true}
                                submitButtonText={'Save'}
                                onSubmit={onSubmit}
                                onFormValidated={() => setWasFormValidated(true)}
                                additionalSubmitButtonClassname="btn-full-width"
                            >
                                <FormValidationDisplay errorMessage={errorMessage} />
                                <label className="prospect-guidance-header">My Profile</label>
                                <section className="bp-1">
                                    <span>
                                        Phone Number: {formatPhoneNumber(profileData.phone)}
                                    </span>
                                </section>
                                <ProfilePageInput
                                    labelText="First Name"
                                    invalidText="Please enter your first name"
                                    id="first-name"
                                    name="firstName"
                                    value={profileData && profileData.firstName}
                                    onChange={onInputChange}
                                />
                                <ProfilePageInput
                                    labelText="Last Name"
                                    invalidText="Please enter your last name"
                                    id="last-name"
                                    name="lastName"
                                    value={profileData && profileData.lastName}
                                    onChange={onInputChange}
                                />
                                <ProfilePageInput
                                    labelText="Email"
                                    invalidText="Please enter your email address"
                                    id="email"
                                    name="email"
                                    value={profileData && profileData.email}
                                    onChange={onInputChange}
                                />
                                <ContactPreferencesCheckboxes
                                    preferenceValue={profileData.contactPreference}
                                    setPreferenceValue={(preferenceValue: number) =>
                                        setProfileData({
                                            ...profileData,
                                            contactPreference: preferenceValue
                                        })
                                    }
                                />
                            </Form>
                            <Button
                                text="Delete Account"
                                onClick={() => setDeletionModalOpen(true)}
                                addlClassName="delete-account-button"
                            />
                        </div>
                    )}
                </Col>
            </Row>
            <Modal isOpen={deletionModalOpen}>
                <ModalBody>
                    <FormValidationDisplay errorMessage={deletionError} />
                    {deletionLoading ? (
                        <LoadingDisplay />
                    ) : (
                        <div>
                            <h4>Are you sure you want to delete your account?</h4>
                            <p>
                                You will not be able to access any past or future tour information.
                            </p>
                            <div className="deletion-popup-buttons">
                                <Button
                                    text="Yes, Delete My Account"
                                    onClick={() => handleDeleteUser()}
                                    addlClassName="btn-danger"
                                    suppressBottomMargin={true}
                                />
                                <Button
                                    text="Cancel"
                                    onClick={() => setDeletionModalOpen(false)}
                                    addlClassName="deletion-cancel"
                                    suppressBottomMargin={true}
                                />
                            </div>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};

export default MyProfilePage;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/account/MyProfilePage.tsx